import { useMaintenancePlans, useMaintenanceLogs, WORK_TYPE_LABELS } from '@/hooks/useAssetMaintenance';
import { useWorkCosts } from '@/hooks/useAssetFinancials';
import { usePermissions } from '@/hooks/usePermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format, formatDistanceToNow, isPast, isFuture, addDays } from 'date-fns';
import { Calendar, Wrench, AlertTriangle, CheckCircle, CalendarPlus } from 'lucide-react';
import { MaintenanceLogForm } from './MaintenanceLogForm';
import { MaintenancePlanForm } from './MaintenancePlanForm';

interface AssetMaintenanceTabProps {
  assetId: string;
}

export function AssetMaintenanceTab({ assetId }: AssetMaintenanceTabProps) {
  const { data: plans, isLoading: loadingPlans } = useMaintenancePlans(assetId);
  const { data: logs, isLoading: loadingLogs } = useMaintenanceLogs(assetId);
  const { data: workCosts } = useWorkCosts(assetId);
  const { isManager } = usePermissions();

  // Get costs by log ID for display
  const costsByLogId = (workCosts ?? []).reduce((acc, c) => {
    acc[c.log_id] = c.cost;
    return acc;
  }, {} as Record<string, number>);

  // Split plans into overdue, due soon (7 days), and upcoming
  const overduePlans = (plans ?? []).filter(p => p.active && isPast(new Date(p.next_due_at)));
  const dueSoonPlans = (plans ?? []).filter(p => {
    if (!p.active) return false;
    const dueDate = new Date(p.next_due_at);
    return isFuture(dueDate) && dueDate <= addDays(new Date(), 7);
  });

  if (loadingPlans || loadingLogs) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {overduePlans.length > 0 && (
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-800 dark:text-red-200">
              <AlertTriangle className="h-4 w-4" />
              Overdue Maintenance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {overduePlans.map(plan => (
                <li key={plan.id} className="text-sm text-red-700 dark:text-red-300">
                  <strong>{plan.name}</strong> — was due {formatDistanceToNow(new Date(plan.next_due_at))} ago
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {dueSoonPlans.length > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <Calendar className="h-4 w-4" />
              Due Soon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {dueSoonPlans.map(plan => (
                <li key={plan.id} className="text-sm text-amber-700 dark:text-amber-300">
                  <strong>{plan.name}</strong> — due in {formatDistanceToNow(new Date(plan.next_due_at))}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Log Work Button */}
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Work History</h3>
        <div className="flex gap-2">
          <MaintenancePlanForm assetId={assetId} />
          <MaintenanceLogForm assetId={assetId} />
        </div>
      </div>

      {/* Work History */}
      {(logs ?? []).length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Wrench className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No work logged yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {(logs ?? []).map(log => (
            <Card key={log.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary">{WORK_TYPE_LABELS[log.work_type]}</Badge>
                        {log.plan && (
                          <span className="text-xs text-muted-foreground">from {log.plan.name}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {format(new Date(log.performed_at), 'MMM d, yyyy h:mm a')}
                        {log.performer && ` • ${log.performer.display_name}`}
                      </p>
                      {log.notes && (
                        <p className="text-sm mt-2 whitespace-pre-wrap">{log.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Cost - Manager Only */}
                  {isManager && costsByLogId[log.id] !== undefined && (
                    <Badge variant="outline" className="font-mono">
                      ${costsByLogId[log.id].toLocaleString()}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Maintenance Plans */}
      {(plans ?? []).length > 0 && (
        <>
          <h3 className="font-medium mt-8">Scheduled Plans</h3>
          <div className="space-y-3">
            {(plans ?? []).filter(p => p.active).map(plan => (
              <Card key={plan.id}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{plan.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Every {plan.interval_days} days • Next due {format(new Date(plan.next_due_at), 'MMM d, yyyy')}
                      </p>
                      {plan.description && (
                        <p className="text-sm mt-1">{plan.description}</p>
                      )}
                    </div>
                    <Badge 
                      variant={isPast(new Date(plan.next_due_at)) ? 'destructive' : 'secondary'}
                    >
                      {isPast(new Date(plan.next_due_at)) ? 'Overdue' : 'Scheduled'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
